import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import teamChatService, { type TeamChatDirectoryUser } from '../../../services/teamChatService';
import TeamChatAvatar from './TeamChatAvatar';
import { getTeamChatDockZ, TEAM_CHAT_THEME, teamChatSearchFieldSx, teamChatUnitChipSx } from '../teamChatTheme';
import { SYSTEM_ROLE_LABELS } from '../teamChatUtils';

interface TeamChatNewConversationDialogProps {
  open: boolean;
  onlineUserIds: Set<string>;
  onClose: () => void;
  onStartDirect: (userId: string) => Promise<void>;
  onCreateGroup: (payload: { title: string; description?: string; participant_ids: string[] }) => Promise<void>;
}

const TeamChatNewConversationDialog: React.FC<TeamChatNewConversationDialogProps> = ({
  open,
  onlineUserIds,
  onClose,
  onStartDirect,
  onCreateGroup,
}) => {
  const theme = useTheme();
  const dockZ = getTeamChatDockZ(theme);
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<TeamChatDirectoryUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [groupTitle, setGroupTitle] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setSearch('');
    setSelected([]);
    setGroupTitle('');
    setGroupDescription('');
    setError(null);

    setLoading(true);
    teamChatService
      .getDirectory()
      .then(setUsers)
      .catch((e: any) => setError(e?.response?.data?.error || 'Não foi possível carregar a equipe.'))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => {
      const haystack = [u.full_name, u.email, ...u.units.map((unit) => unit.name)].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [users, search]);

  /** Agrupa por unidade para deixar explícito quem é de qual unidade. */
  const grouped = useMemo(() => {
    const map = new Map<string, TeamChatDirectoryUser[]>();
    for (const user of filtered) {
      const label = user.units[0]?.name || 'Sem unidade vinculada';
      const list = map.get(label) || [];
      list.push(user);
      map.set(label, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const handleUserClick = async (user: TeamChatDirectoryUser) => {
    if (tab === 1) {
      setSelected((prev) =>
        prev.includes(user.id) ? prev.filter((id) => id !== user.id) : [...prev, user.id],
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onStartDirect(user.id);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível abrir a conversa.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupTitle.trim()) {
      setError('Informe um nome para o grupo.');
      return;
    }
    if (selected.length === 0) {
      setError('Selecione ao menos um participante.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreateGroup({
        title: groupTitle.trim(),
        description: groupDescription.trim() || undefined,
        participant_ids: selected,
      });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível criar o grupo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: dockZ.overlay }}
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '86vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          fontWeight: 700,
          fontSize: '1.02rem',
          color: TEAM_CHAT_THEME.navy,
          letterSpacing: '-0.02em',
        }}
      >
        <Box sx={{ flex: 1 }}>Nova conversa</Box>
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_e, value) => setTab(value)}
        sx={{
          px: 3,
          minHeight: 40,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', minHeight: 40 },
        }}
      >
        <Tab label="Conversa direta" />
        <Tab label="Novo grupo" />
      </Tabs>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {tab === 1 && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Nome do grupo"
              placeholder="Ex.: Recepção — Unidade Centro"
              value={groupTitle}
              onChange={(e) => setGroupTitle(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Descrição (opcional)"
              placeholder="Assunto ou objetivo do grupo"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
            />
            {selected.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const user = users.find((u) => u.id === id);
                  return (
                    <Chip
                      key={id}
                      size="small"
                      label={user?.full_name || 'Usuário'}
                      onDelete={() => setSelected((prev) => prev.filter((x) => x !== id))}
                      sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por nome, e-mail ou unidade"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={teamChatSearchFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: TEAM_CHAT_THEME.slateFaint }} />
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Typography sx={{ mt: 1.5, fontSize: '0.8rem', color: TEAM_CHAT_THEME.cancelledText, fontWeight: 500 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={26} />
          </Box>
        ) : grouped.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', fontSize: '0.85rem', color: TEAM_CHAT_THEME.slate }}>
            Nenhum colega encontrado.
          </Typography>
        ) : (
          grouped.map(([unitLabel, unitUsers]) => (
            <Box key={unitLabel} sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: TEAM_CHAT_THEME.slateFaint,
                  mb: 0.5,
                }}
              >
                {unitLabel}
              </Typography>
              <List disablePadding>
                {unitUsers.map((user) => {
                  const isSelected = selected.includes(user.id);
                  return (
                    <ListItem
                      key={user.id}
                      onClick={() => !submitting && handleUserClick(user)}
                      sx={{
                        cursor: submitting ? 'default' : 'pointer',
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 1.25,
                        bgcolor: isSelected ? TEAM_CHAT_THEME.primarySoft : 'transparent',
                        '&:hover': { bgcolor: TEAM_CHAT_THEME.mutedAlt },
                      }}
                    >
                      <TeamChatAvatar
                        name={user.full_name}
                        avatarUrl={user.avatar_url}
                        gender={user.gender}
                        online={onlineUserIds.has(user.id) || Boolean(user.online)}
                        size={38}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{ fontSize: '0.86rem', fontWeight: 600, color: TEAM_CHAT_THEME.navy }}
                        >
                          {user.full_name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                          <Typography sx={{ fontSize: '0.72rem', color: TEAM_CHAT_THEME.slate }}>
                            {SYSTEM_ROLE_LABELS[user.role] || user.role}
                          </Typography>
                          {user.units.slice(1).map((unit) => (
                            <Chip key={unit.id} size="small" label={unit.short_name} sx={teamChatUnitChipSx} />
                          ))}
                        </Box>
                      </Box>
                      {tab === 1 &&
                        (isSelected ? (
                          <CheckCircleIcon sx={{ fontSize: 20, color: TEAM_CHAT_THEME.primary }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: TEAM_CHAT_THEME.slateFaint }} />
                        ))}
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))
        )}
      </DialogContent>

      {tab === 1 && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: TEAM_CHAT_THEME.primary,
              '&:hover': { bgcolor: '#1D4ED8' },
            }}
          >
            {submitting ? 'Criando…' : `Criar grupo${selected.length ? ` (${selected.length})` : ''}`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TeamChatNewConversationDialog;
